import { ArrowUpRight, Sparkles, ThumbsDown, ThumbsUp, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';

import { useAsistenteStore, ROLE_LABELS } from '../asistente.store';
import { asistenteApi } from '../asistente.api';
import type { ChatMessage } from '../asistente.types';
import { AssistantAction } from './assistant-action';
import { AssistantChart } from './assistant-chart';
import { AssistantTable } from './assistant-table';
import { KpiCards } from './kpi-cards';
import { MarkdownContent } from './markdown-content';

interface ChatMessageBubbleProps {
  message: ChatMessage;
}

/** Formatea el nombre interno de una herramienta para mostrarlo al usuario. */
const toolLabel = (name: string): string => name.replace(/_/g, ' ');

/**
 * Burbuja de un mensaje del hilo.
 *
 * - `user`: alineado a la derecha, fondo de marca.
 * - `assistant`: con avatar, texto en markdown, tarjetas y gráficos.
 * - `error`: variante en rojo, sin perder el hilo de la conversación.
 */
export function ChatMessageBubble({ message }: ChatMessageBubbleProps) {
  const setFeedback = useAsistenteStore((state) => state.setFeedback);

  const isUser = message.role === 'user';
  const isError = message.role === 'error';

  const tools = message.tools ?? [];
  const cards = message.cards ?? [];
  const charts = message.charts ?? [];
  const tablas = message.tablas ?? [];
  const enlaces = message.enlaces ?? [];
  const acciones = message.acciones ?? [];

  /** Guarda la valoración sin bloquear la interfaz (alimenta la mejora del asistente). */
  const valorar = (util: boolean) => {
    if (!message.consultaId) return;

    setFeedback(message.id, util);
    void asistenteApi.feedback(message.consultaId, util).catch(() => undefined);
  };

  return (
    <article
      className={cn('flex w-full gap-3', isUser ? 'justify-end' : 'justify-start')}
      aria-label={ROLE_LABELS[message.role]}
    >
      {!isUser ? (
        <span
          aria-hidden="true"
          className={cn(
            'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full text-white',
            isError ? 'bg-rose-500' : 'bg-gradient-to-br from-brand-500 to-accent-600',
          )}
        >
          {isError ? <TriangleAlert className="size-4" /> : <Sparkles className="size-4" />}
        </span>
      ) : null}

      <div
        className={cn(
          'min-w-0',
          isUser
            ? 'max-w-[85%] rounded-2xl rounded-br-md bg-gradient-to-br from-brand-500 to-accent-600 px-4 py-2.5 text-sm text-white shadow-brand'
            : 'max-w-full flex-1 rounded-2xl rounded-bl-md border px-4 py-3',
          !isUser && (isError ? 'border-rose-200 bg-rose-50' : 'border-ink-100 bg-white shadow-soft'),
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
        ) : (
          <MarkdownContent text={message.content} className={isError ? 'text-rose-800' : undefined} />
        )}

        <KpiCards cards={cards} />

        {charts.length > 0 ? (
          <div className="mt-3 space-y-1">
            {charts.map((chart, index) => (
              <AssistantChart key={`${chart.title ?? 'chart'}-${index}`} spec={chart} />
            ))}
          </div>
        ) : null}

        {tablas.map((tabla, index) => (
          <AssistantTable key={`${tabla.titulo}-${index}`} tabla={tabla} />
        ))}

        {acciones.map((accion) => (
          <AssistantAction key={`${accion.id}-${JSON.stringify(accion.params)}`} accion={accion} />
        ))}

        {enlaces.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {enlaces.map((enlace) => (
              <Link
                key={enlace.ruta}
                to={enlace.ruta}
                className={cn(
                  'inline-flex items-center gap-1 rounded-lg border border-ink-100 bg-white px-2.5 py-1',
                  'text-xs font-medium text-ink-600 transition-colors hover:border-brand-200 hover:text-brand-600',
                )}
              >
                {enlace.etiqueta}
                <ArrowUpRight className="size-3.5" aria-hidden="true" />
              </Link>
            ))}
          </div>
        ) : null}

        {tools.length > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-2.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-400">
              Consultó
            </span>

            {tools.map((tool) => (
              <span
                key={tool}
                className="rounded-full bg-ink-100 px-2 py-0.5 font-mono text-[10px] text-ink-600"
              >
                {toolLabel(tool)}
              </span>
            ))}

            {!isError && message.consultaId ? (
              <span className="ml-auto flex items-center gap-1">
                <span className="text-[10px] text-ink-400">
                  {message.feedback === undefined ? '¿Te sirvió?' : 'Gracias'}
                </span>

                <button
                  type="button"
                  aria-label="Respuesta útil"
                  onClick={() => valorar(true)}
                  disabled={message.feedback !== undefined}
                  className={cn(
                    'cursor-pointer rounded-md p-1 transition-colors',
                    message.feedback === true ? 'bg-emerald-100 text-emerald-600' : 'text-ink-400 hover:text-emerald-600',
                    'disabled:cursor-default',
                  )}
                >
                  <ThumbsUp className="size-3.5" />
                </button>

                <button
                  type="button"
                  aria-label="Respuesta poco útil"
                  onClick={() => valorar(false)}
                  disabled={message.feedback !== undefined}
                  className={cn(
                    'cursor-pointer rounded-md p-1 transition-colors',
                    message.feedback === false ? 'bg-rose-100 text-rose-600' : 'text-ink-400 hover:text-rose-600',
                    'disabled:cursor-default',
                  )}
                >
                  <ThumbsDown className="size-3.5" />
                </button>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
}
